import { AppLayout } from '@/components/layout/app-layout';
import { FadeIn } from '@/components/motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useAuth } from '@/features/auth/hooks/use-auth';
import {
  empresaTieneAlgunModulo,
  MODULOS_QUE_USAN_INTEGRACIONES,
} from '@/features/auth/tenant-modules';
import { SalesProfileEditor } from '@/features/sales-training/components/sales-profile-editor';
import { SalesExamplesTable } from '@/features/sales-training/components/sales-examples-table';
import { Settings, Bell, Palette, Info, Bot, Plug } from 'lucide-react';

import { IntegrationsPage } from '@/features/integrations/pages/integrations-page';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { useThemeStore } from '@/stores/theme-store';
import { EncabezadoDeMiEmpresa } from '@/features/mi-empresa/components/encabezado-de-mi-empresa';
import { CompanyInfoCard } from '@/features/settings/components/company-info-card';

export function SettingsPage() {
  const { user, isAdmin } = useAuth();
  // 2.7c: el servidor da 403 a `/api/integrations` si la empresa no tiene el módulo (ni Cotiza ni Datos Maestros):
  // sin ese techo la pestaña no se ofrece. Entrenamiento IA es global: la ve todo administrador.
  const verIntegraciones = isAdmin && empresaTieneAlgunModulo(MODULOS_QUE_USAN_INTEGRACIONES);
  const { theme, setTheme } = useThemeStore();

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        <EncabezadoDeMiEmpresa
          activa="configuracion"
          dataTour="settings-header"
          titulo="Configuración"
          subtitulo="Los ajustes de tu empresa y de tu cuenta."
        />
        <div>
          <Tabs defaultValue="general">
            <TabsList>
              <TabsTrigger value="general">
                <Settings className="h-4 w-4 mr-2" />
                General
              </TabsTrigger>
              {/*
                Permisos y Aprobaciones salieron de acá (18-oct, artefacto): son
                pestañas de Mi empresa, arriba de esta misma pantalla, con su
                dirección (/permisos, /configuracion/aprobaciones).
              */}
              {isAdmin && (
                <TabsTrigger value="ai-training">
                  <Bot className="h-4 w-4 mr-2" />
                  Entrenamiento IA
                </TabsTrigger>
              )}
              {verIntegraciones && (
                <TabsTrigger value="integrations">
                  <Plug className="h-4 w-4 mr-2" />
                  Integraciones
                </TabsTrigger>
              )}
            </TabsList>

            {/* Tab General */}
            <TabsContent value="general">
              {/*
                Dos columnas de verdad, no un grid en flujo.

                Antes las cinco cards caían en `grid-cols-2` en el orden del DOM, y
                el resultado era una grilla intercalada: la empresa arriba a la
                izquierda, el formato numérico a su derecha, las notificaciones
                debajo de la empresa. Nada de eso agrupaba nada — la altura de una
                card empujaba a la de al lado y la lectura saltaba de columna.

                Ahora cada columna es una idea: a la izquierda lo que se ajusta
                (formato, notificaciones, apariencia, la versión), a la derecha
                quién es la empresa. `items-start` para que la columna corta no se
                estire a la altura de la larga.
              */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4 items-start">
                {/* ── Izquierda: lo que se ajusta ── */}
                <div className="space-y-6">
                  {/* Configuración Numérica */}
                  {isAdmin && (
                    <FadeIn delay={0.15}>
                      <Card>
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <Settings className="h-5 w-5 text-muted-foreground" />
                            <CardTitle>Formato numérico</CardTitle>
                          </div>
                          <CardDescription>
                            Cantidad de decimales para precios y cálculos en todo el sistema
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-4">
                            <label className="text-sm font-medium">Decimales:</label>
                            <SearchableSelect
                              value={String(user?.tenant?.decimalPlaces ?? 2)}
                              onValueChange={async (val) => {
                                const dp = Number(val);
                                try {
                                  const { apiClient: api } = await import('@/lib/api');
                                  await api.patch(`/api/admin/tenants/${user?.tenantId}`, {
                                    decimalPlaces: dp,
                                  });
                                  const { setGlobalDecimalPlaces: setDP } =
                                    await import('@/lib/utils/format');
                                  setDP(dp);
                                  window.location.reload();
                                } catch {
                                  /* ignore */
                                }
                              }}
                              options={[
                                { value: '0', label: '0 — Sin decimales (100)' },
                                { value: '2', label: '2 — Estándar (100.00)' },
                                { value: '3', label: '3 — Tres decimales (100.000)' },
                                { value: '4', label: '4 — Alta precisión (100.0000)' },
                              ]}
                              placeholder="Seleccionar..."
                              className="w-56"
                            />
                            <span className="text-xs text-muted-foreground">
                              Aplica a cotizaciones, precios y reportes
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </FadeIn>
                  )}

                  {/* Preferencias de Notificaciones */}
                  <FadeIn delay={0.2}>
                    <Card data-tour="settings-preferences">
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <Bell className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>Notificaciones</CardTitle>
                        </div>
                        <CardDescription>Dónde te avisa Prixia</CardDescription>
                      </CardHeader>
                      {/*
                        18-oct: acá había tres casillas (correo, push, sonido) y la
                        frase «se guardarán localmente». No se guardaban ni las leía
                        nadie: al volver a entrar estaban como al principio. Una
                        casilla que no hace nada enseña que la pantalla miente; se
                        dice lo que hoy pasa de verdad.
                      */}
                      <CardContent className="space-y-2 text-sm">
                        <p>
                          Los avisos llegan a la campana de arriba: aprobaciones, cambios de la
                          competencia y lo que se te comparte.
                        </p>
                        <p className="text-muted-foreground">
                          Elegir que lleguen también por correo todavía no está disponible.
                        </p>
                      </CardContent>
                    </Card>
                  </FadeIn>

                  {/* Apariencia */}
                  <FadeIn delay={0.3}>
                    <Card>
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <Palette className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>Apariencia</CardTitle>
                        </div>
                        <CardDescription>
                          Claro u oscuro, en este navegador. Es lo mismo que la luna de arriba.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <GrupoDeOpciones
                          aria-label="Tema de la aplicación"
                          valor={theme}
                          onCambiar={setTheme}
                          opciones={[
                            { valor: 'light', texto: 'Claro' },
                            { valor: 'dark', texto: 'Oscuro' },
                            { valor: 'system', texto: 'Como el sistema' },
                          ]}
                        />
                      </CardContent>
                    </Card>
                  </FadeIn>

                  {/* Acerca de */}
                  <FadeIn delay={0.4}>
                    <Card>
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <Info className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>Acerca de</CardTitle>
                        </div>
                        <CardDescription>Información de la plataforma</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Plataforma</p>
                            <p className="text-sm mt-1">Prixia Platform</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Versión</p>
                            <p className="text-sm mt-1">1.0.0</p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Desarrollado con Express, React, Prisma y PostgreSQL.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </FadeIn>
                </div>

                {/* ── Derecha: la empresa ── */}
                <div className="space-y-6">
                  {/* Información de la empresa */}
                  <FadeIn delay={0.1}>
                    <CompanyInfoCard />
                  </FadeIn>
                </div>
              </div>
            </TabsContent>

            {/* Tab Entrenamiento IA */}
            {isAdmin && (
              <TabsContent value="ai-training">
                <div className="space-y-6 mt-4">
                  <FadeIn>
                    <SalesProfileEditor />
                  </FadeIn>
                  <FadeIn delay={0.1}>
                    <SalesExamplesTable />
                  </FadeIn>
                </div>
              </TabsContent>
            )}

            {/* Tab Integraciones */}
            {verIntegraciones && (
              <TabsContent value="integrations">
                <div className="mt-4">
                  <FadeIn>
                    <IntegrationsPage />
                  </FadeIn>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </AppLayout>
  );
}

export default SettingsPage;
